import type { FilterState } from "@/utils/catalog";
import {
  GENRE_OPTIONS,
  REGION_OPTIONS,
  YEAR_OPTIONS,
  RATING_OPTIONS,
  countActiveFilters,
} from "@/utils/catalog";

interface FilterPanelProps {
  filters: FilterState;
  onChange: (next: FilterState) => void;
  onReset: () => void;
}

function toggleValue<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

export default function FilterPanel({ filters, onChange, onReset }: FilterPanelProps) {
  const activeCount = countActiveFilters(filters);

  const chipClass = (active: boolean) =>
    `whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer ${
      active
        ? "border-primary-500 bg-primary-100 text-primary-800"
        : "border-background-300 text-foreground-600 hover:border-primary-300 hover:text-primary-700"
    }`;

  return (
    <div className="rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-6">
      <div className="mb-5 flex items-center justify-between">
        <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
          <i className="ri-filter-3-line text-primary-600"></i>
          筛选条件
          {activeCount > 0 && (
            <span className="rounded-full bg-accent-500 px-2 py-0.5 text-[11px] font-medium text-background-50">
              {activeCount}
            </span>
          )}
        </h2>
        <button
          type="button"
          onClick={onReset}
          className="whitespace-nowrap text-xs font-medium text-foreground-500 transition-colors hover:text-accent-600 cursor-pointer"
        >
          重置筛选
        </button>
      </div>

      <div className="flex flex-col gap-5">
        <div>
          <p className="mb-2.5 text-xs font-semibold uppercase tracking-wider text-foreground-400">类型</p>
          <div className="flex flex-wrap gap-2">
            {GENRE_OPTIONS.map((genre) => (
              <button
                key={genre}
                type="button"
                onClick={() => onChange({ ...filters, genres: toggleValue(filters.genres, genre) })}
                className={chipClass(filters.genres.includes(genre))}
              >
                {genre}
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="mb-2.5 text-xs font-semibold uppercase tracking-wider text-foreground-400">地区</p>
          <div className="flex flex-wrap gap-2">
            {REGION_OPTIONS.map((region) => (
              <button
                key={region}
                type="button"
                onClick={() => onChange({ ...filters, regions: toggleValue(filters.regions, region) })}
                className={chipClass(filters.regions.includes(region))}
              >
                {region}
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="mb-2.5 text-xs font-semibold uppercase tracking-wider text-foreground-400">年份</p>
          <div className="flex flex-wrap gap-2">
            {YEAR_OPTIONS.map((year) => (
              <button
                key={year}
                type="button"
                onClick={() => onChange({ ...filters, years: toggleValue(filters.years, year) })}
                className={chipClass(filters.years.includes(year))}
              >
                {year}
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="mb-2.5 text-xs font-semibold uppercase tracking-wider text-foreground-400">评分区间</p>
          <div className="flex flex-wrap gap-2">
            {RATING_OPTIONS.map((option) => (
              <button
                key={option.label}
                type="button"
                onClick={() =>
                  onChange({
                    ...filters,
                    ratingMin: filters.ratingMin === option.value ? null : option.value,
                  })
                }
                className={chipClass(filters.ratingMin === option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}